import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Home, Building2, Users, Calendar, FileText,
  BarChart3, Settings, LogOut, Menu, X, Bell,
  Megaphone, Sun, Moon, CalendarDays, Receipt,
  Clock3, ArrowLeftRight, Scan, Star, ChevronLeft,
  Shield, History, Fingerprint, UsersRound, MapPin, MessageSquare,
  FileCheck2, UserCircle, HardDrive, Server, TrendingUp
} from 'lucide-react';

const SidebarNavigation = ({ isOpen, onClose, isAdmin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const adminNavItems = [
    { section: 'Utama', items: [
      { path: '/admin', label: 'Papan Utama', icon: Home },
    ]},
    { section: 'Pengurusan', items: [
      { path: '/branches', label: 'Cawangan', icon: Building2 },
      { path: '/staff', label: 'Staf', icon: Users },
      { path: '/team', label: 'Status Pasukan', icon: UsersRound },
      { path: '/schedule', label: 'Jadual Harian', icon: CalendarDays },
      { path: '/location-tracking', label: 'Lokasi Staf', icon: MapPin },
    ]},
    { section: 'Cuti & Kerja', items: [
      { path: '/leave-manage', label: 'Pengurusan Cuti', icon: Calendar },
      { path: '/leave-calendar', label: 'Kalendar Cuti', icon: CalendarDays },
      { path: '/leave-history', label: 'Rekod Baki Cuti', icon: History },
      { path: '/overtime-approval', label: 'Kelulusan OT', icon: Clock3 },
      { path: '/shift-swap', label: 'Tukar Shift', icon: ArrowLeftRight },
    ]},
    { section: 'Laporan', items: [
      { path: '/reports', label: 'Laporan Kehadiran', icon: FileText },
      { path: '/crosscheck', label: 'Cross-Check', icon: FileCheck2 },
      { path: '/weekly-summary', label: 'Ringkasan Mingguan', icon: TrendingUp },
      { path: '/carry-hour', label: 'Carry Hour', icon: Clock3 },
      { path: '/carry-ot', label: 'Rekod CH & OT', icon: CalendarDays },
      { path: '/analytics', label: 'Analitik', icon: BarChart3 },
      { path: '/payslip', label: 'Slip Gaji', icon: Receipt },
      { path: '/whatsapp', label: 'WhatsApp', icon: MessageSquare },
    ]},
    { section: 'Lain-lain', items: [
      { path: '/holidays', label: 'Cuti Umum', icon: Star },
      { path: '/announcements', label: 'Pengumuman', icon: Megaphone },
      { path: '/face-records', label: 'Rekod Wajah', icon: Scan },
      { path: '/work-days', label: 'Hari Kerja Bulanan', icon: CalendarDays },
    ]},
    { section: 'Keselamatan', items: [
      { path: '/audit-log', label: 'Log Audit', icon: History },
      { path: '/security', label: 'Tetapan Keselamatan', icon: Shield },
      { path: '/backup', label: 'Backup & Restore', icon: HardDrive },
      { path: '/system-status', label: 'Status Sistem', icon: Server },
    ]},
    { section: 'Tetapan', items: [
      { path: '/biodata', label: 'Biodata', icon: UserCircle },
      { path: '/settings', label: 'Tetapan Umum', icon: Settings },
    ]},
  ];

  const staffNavItems = [
    { section: 'Utama', items: [
      { path: '/dashboard', label: 'Papan Utama', icon: Home },
      { path: '/team', label: 'Status Pasukan', icon: UsersRound },
      { path: '/face-register', label: 'Daftar Wajah', icon: Scan },
    ]},
    { section: 'Permohonan', items: [
      { path: '/leave', label: 'Permohonan Cuti', icon: Calendar },
      { path: '/leave-calendar', label: 'Kalendar Cuti', icon: CalendarDays },
      { path: '/overtime', label: 'Permohonan OT', icon: Clock3 },
      { path: '/shift-swap', label: 'Tukar Shift', icon: ArrowLeftRight },
    ]},
    { section: 'Maklumat', items: [
      { path: '/schedule', label: 'Jadual Saya', icon: CalendarDays },
      { path: '/weekly-summary', label: 'Ringkasan Mingguan', icon: TrendingUp },
      { path: '/carry-hour', label: 'Carry Hour Saya', icon: Clock3 },
      { path: '/carry-ot', label: 'Rekod CH & OT', icon: CalendarDays },
      { path: '/holidays', label: 'Cuti Umum', icon: Star },
      { path: '/announcements', label: 'Pengumuman', icon: Megaphone },
    ]},
    { section: 'Tetapan', items: [
      { path: '/biodata', label: 'Biodata Saya', icon: UserCircle },
      { path: '/security', label: 'Keselamatan', icon: Shield },
      { path: '/settings', label: 'Tetapan', icon: Settings },
    ]},
  ];

  const supervisorNavItems = [
    { section: 'Utama', items: [
      { path: '/dashboard', label: 'Clock In/Out', icon: Clock3 },
      { path: '/admin', label: 'Papan Utama', icon: Home },
      { path: '/team', label: 'Status Pasukan', icon: UsersRound },
    ]},
    { section: 'Pengurusan', items: [
      { path: '/staff', label: 'Staf', icon: Users },
      { path: '/schedule', label: 'Jadual Harian', icon: CalendarDays },
    ]},
    { section: 'Permohonan Peribadi', items: [
      { path: '/face-register', label: 'Daftar Wajah', icon: Scan },
    ]},
    { section: 'Lihat Permohonan', items: [
      { path: '/leave', label: 'Permohonan Cuti', icon: Calendar },
      { path: '/overtime', label: 'Permohonan OT', icon: Clock3 },
      { path: '/shift-swap', label: 'Tukar Shift', icon: ArrowLeftRight },
      { path: '/leave-manage', label: 'Pengurusan Cuti', icon: Calendar },
      { path: '/leave-calendar', label: 'Kalendar Cuti', icon: CalendarDays },
      { path: '/overtime-approval', label: 'Kelulusan OT', icon: Clock3 },
    ]},
    { section: 'Laporan', items: [
      { path: '/reports', label: 'Laporan Kehadiran', icon: FileText },
      { path: '/weekly-summary', label: 'Ringkasan Mingguan', icon: TrendingUp },
      { path: '/carry-hour', label: 'Carry Hour', icon: Clock3 },
      { path: '/carry-ot', label: 'Rekod CH & OT', icon: CalendarDays },
    ]},
    { section: 'Lain-lain', items: [
      { path: '/holidays', label: 'Cuti Umum', icon: Star },
      { path: '/announcements', label: 'Pengumuman', icon: Megaphone },
    ]},
    { section: 'Keselamatan', items: [
      { path: '/security', label: 'Tetapan Keselamatan', icon: Shield },
    ]},
    { section: 'Tetapan', items: [
      { path: '/biodata', label: 'Biodata', icon: UserCircle },
      { path: '/settings', label: 'Tetapan Umum', icon: Settings },
    ]},
  ];

  const branchManagerNavItems = [
    { section: 'Utama', items: [
      { path: '/admin', label: 'Papan Utama', icon: Home },
    ]},
    { section: 'Pengurusan', items: [
      { path: '/branches', label: 'Cawangan', icon: Building2 },
      { path: '/staff', label: 'Staf', icon: Users },
      { path: '/schedule', label: 'Jadual Harian', icon: CalendarDays },
      { path: '/location-tracking', label: 'Lokasi Staf', icon: MapPin },
    ]},
    { section: 'Cuti & Kerja', items: [
      { path: '/leave-manage', label: 'Pengurusan Cuti', icon: Calendar },
      { path: '/leave-calendar', label: 'Kalendar Cuti', icon: CalendarDays },
      { path: '/leave-history', label: 'Rekod Baki Cuti', icon: History },
      { path: '/overtime-approval', label: 'Kelulusan OT', icon: Clock3 },
      { path: '/shift-swap', label: 'Tukar Shift', icon: ArrowLeftRight },
    ]},
    { section: 'Laporan', items: [
      { path: '/reports', label: 'Laporan Kehadiran', icon: FileText },
      { path: '/crosscheck', label: 'Cross-Check', icon: FileCheck2 },
      { path: '/weekly-summary', label: 'Ringkasan Mingguan', icon: TrendingUp },
      { path: '/carry-hour', label: 'Carry Hour', icon: Clock3 },
      { path: '/carry-ot', label: 'Rekod CH & OT', icon: CalendarDays },
      { path: '/analytics', label: 'Analitik', icon: BarChart3 },
      { path: '/payslip', label: 'Slip Gaji', icon: Receipt },
      { path: '/whatsapp', label: 'WhatsApp', icon: MessageSquare },
    ]},
    { section: 'Lain-lain', items: [
      { path: '/holidays', label: 'Cuti Umum', icon: Star },
      { path: '/announcements', label: 'Pengumuman', icon: Megaphone },
      { path: '/work-days', label: 'Hari Kerja Bulanan', icon: CalendarDays },
    ]},
    { section: 'Keselamatan', items: [
      { path: '/audit-log', label: 'Log Audit', icon: History },
      { path: '/security', label: 'Tetapan Keselamatan', icon: Shield },
    ]},
    { section: 'Tetapan', items: [
      { path: '/biodata', label: 'Biodata', icon: UserCircle },
      { path: '/settings', label: 'Tetapan Umum', icon: Settings },
    ]},
  ];

  const navSections = user?.role === 'branch_manager' ? branchManagerNavItems : (user?.role === 'supervisor' ? supervisorNavItems : (isAdmin ? adminNavItems : staffNavItems));

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          />
          
          {/* Sidebar */}
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-72 z-50 glass dark:bg-stone-900/95 border-r border-orange-100 dark:border-stone-800 shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 border-b border-orange-100 dark:border-stone-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl overflow-hidden shadow-md">
                    <img src="/logo.jpeg" alt="My Destiny Junior" className="w-full h-full object-cover" loading="eager" onError={(e) => { e.target.onerror = null; e.target.src = '/logo192.png'; }} />
                  </div>
                  <div>
                    <span className="font-bold text-lg">
                      <span className="gradient-text">My Destiny</span>
                      <span className="text-stone-800 dark:text-stone-100"> Junior</span>
                    </span>
                    <p className="text-xs text-stone-500 dark:text-stone-400">Sistem Kehadiran</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} data-testid="sidebar-close-btn" className="lg:hidden rounded-xl hover:bg-orange-50 dark:hover:bg-stone-800">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-orange-100 dark:border-stone-800">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 dark:bg-stone-800">
                <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 dark:text-stone-100 truncate">{user?.name}</p>
                  <Badge className="badge-orange rounded-lg text-xs mt-1">
                    {user?.role === 'admin' ? 'Pentadbir' : user?.role === 'branch_manager' ? 'Pengurus Cawangan' : user?.role === 'supervisor' ? 'Penyelia' : 'Staf'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 h-[calc(100vh-300px)]">
              <div className="p-4 space-y-6 pb-8">
                {navSections.map((section) => (
                  <div key={section.section}>
                    <p className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-wider mb-2 px-3">
                      {section.section}
                    </p>
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={onClose}
                            data-testid={`sidebar-link-${item.path.replace('/', '')}`}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                              isActive
                                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-200 dark:shadow-orange-900/30'
                                : 'text-stone-600 dark:text-stone-400 hover:bg-orange-50 dark:hover:bg-stone-800 hover:text-orange-600 dark:hover:text-orange-400'
                            }`}
                          >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium text-sm">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="p-4 border-t border-orange-100 dark:border-stone-800 space-y-2">
              <div className="flex items-center justify-between px-3">
                <span className="text-sm text-stone-500 dark:text-stone-400">Tema</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className="rounded-xl hover:bg-orange-50 dark:hover:bg-stone-800"
                >
                  {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-500" />}
                  <span className="ml-2 text-sm">{isDark ? 'Cerah' : 'Gelap'}</span>
                </Button>
              </div>
              <Separator />
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="sidebar-logout-btn"
                className="w-full justify-start gap-3 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950 rounded-xl"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Keluar</span>
              </Button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default SidebarNavigation;
